/*
  KA collection intro — the "going deeper" loading veil.

  Click a link into a collection and a branded veil covers the swap with a
  progress bar that fills to 100%. The bar completes, holds for a beat, then
  the veil fades to an EMPTY page — and only then does the content arrive:
  header down from above, hero in from the right, copy and toolbar, product
  grid up from below.

  The order is the whole design. The previous version overlapped the veil's
  fade with the content's reveal, so everything faded in behind an opaque
  white sheet and appeared all at once the moment it lifted. Veil first, then
  content. They never run together.

  The old "morph" treatment (a cross-document view transition from the clicked
  card image into the collection hero) was removed 2026-08-08. It only read
  correctly when the two images mapped near-perfectly, which is not something
  a card and a wide banner can be relied on to do.

  --- On Horizon's own view transitions ---

  base.css declares `@view-transition { navigation: auto }` unconditionally, so
  every same-origin navigation gets a cross-document transition whether we want
  one or not. With no type stamped, view-transitions.js falls through to
  `page-navigation`, which lifts <main> into its own snapshot group. The veil
  lives outside <main>, so it lands in `root-custom` — which stacks BELOW
  `main-content`. The result is that the outgoing page's <main> is composited
  on top of our veil for the length of the crossfade: the landing-page flash.

  We suppress it by stamping our own type. Nothing in any stylesheet matches
  `html:active-view-transition-type(ka-veil-nav)`, so no element ever gets a
  view-transition-name, no groups are created, and there is nothing to
  composite over the veil. This speaks the protocol view-transitions.js already
  reads rather than fighting it — the same two attributes the old morph path
  used, and the same two product-card-link.js uses for the PDP morph.
*/
(() => {
  /*
    Round 8 (B8-1): kill Horizon's default crossfade on back/forward/reload.

    Our own click-side suppression (data-view-transition-type="ka-veil-nav",
    see onIntroClick below) only ever gets stamped on a click. A browser
    back/forward or a reload never runs that handler, so
    view-transitions.js's own pageswap listener finds no
    [data-view-transition-triggered] element, falls through to the default
    'page-navigation' type, and base.css's unconditional
    `@view-transition { navigation: auto }` plays its crossfade -- the
    outgoing page's <main> ghosts on top of our back-veil for ~700ms.
    Documented on film last round: frames-back/f015-f022.

    Registered here, top-level in the IIFE rather than inside any handler,
    so it is live for every pageswap regardless of how the navigation
    started. Horizon's own pageswap handler
    (assets/view-transitions.js:18-45) still runs on the same event and is
    left untouched -- skipTransition() is safe to call from two listeners;
    whichever fires first cancels it for both.
  */
  window.addEventListener('pageswap', (event) => {
    const navigationType = event.activation && event.activation.navigationType;
    if (event.viewTransition && (navigationType === 'traverse' || navigationType === 'reload')) {
      event.viewTransition.skipTransition();
    }
  });

  /*
    Logo flash on a bfcache back/forward restore, for EVERY page. On a bfcache
    hit the browser restores the page in place and NEVER re-runs the head script
    that builds the arrival veil (layout/theme.liquid) -- so a back nav that hits
    bfcache showed no branded loader at all. Registered here, top-level and
    BEFORE the arm-gated early return below (root not .ka-intro-armed -> return),
    so it also covers the homepage and any non-intro page -- that early return is
    exactly why the bfcache handler further down never runs on those pages.
    Self-contained (inline styles + its own guaranteed-removal timers) so it
    never leans on the arrival choreography and can never be left stuck.
  */
  const showBackLogoFlash = () => {
    try {
      const src = document.documentElement.dataset.kaLogo;
      if (!src) return;
      const veil = document.createElement('div');
      veil.setAttribute('role', 'status');
      veil.setAttribute('aria-label', 'Loading');
      veil.style.cssText =
        'position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;' +
        'justify-content:center;background:var(--ka-off-white,#F6F4F1);opacity:1;' +
        'transition:opacity .3s ease;pointer-events:none;';
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      img.style.cssText = 'width:min(74vw,560px);height:auto;';
      veil.appendChild(img);
      document.documentElement.appendChild(veil);
      const HOLD_MS = 450;
      const FADE_MS = 300;
      window.setTimeout(() => { veil.style.opacity = '0'; }, HOLD_MS);
      window.setTimeout(() => { if (veil.parentNode) veil.remove(); }, HOLD_MS + FADE_MS + 60);
    } catch (e) {}
  };
  /*
    Purge any veil still in the document, plus the classes that hide staged
    content. Top-level and unconditional, because the OUTGOING veil (built by
    onIntroClick below and appended to <body> on the way out) has no removal
    path of its own -- the page navigates away with it up, so it is still
    there, opaque and eating pointer events at z-index 2147483000, when the
    browser restores that page from bfcache. The arm-gated pageshow handler at
    the bottom of this file cannot clean it: it closes over the ARRIVAL veil,
    already detached by then, and it is only registered on pages that armed an
    intro at all.

    Dropping ka-intro-armed is what actually reveals staged content -- every
    staging rule in ka-motion.css is scoped to that class.
  */
  const purgeVeils = () => {
    document.querySelectorAll('.ka-veil').forEach((v) => v.remove());
    document.documentElement.classList.remove(
      'ka-intro-armed',
      'ka-intro-product',
      'ka-scroll-lock'
    );
  };

  /*
    Freeze-time cleanup, so a veil never enters bfcache in the first place.
    pageshow below would remove it anyway, but only after the restore -- this
    closes the window where the stale sheet could paint for a frame. persisted
    only: on a real unload the document is being thrown away regardless.
  */
  window.addEventListener('pagehide', (event) => {
    if (!event.persisted) return;
    purgeVeils();
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    /* Order matters: clear the stale sheet FIRST, then paint the logo over a
       clean page. showBackLogoFlash's own veil is inline-styled and carries no
       .ka-veil class, so the purge cannot eat it. */
    purgeVeils();
    showBackLogoFlash();
  });

  const KEY = 'ka-intro';

  /* Wall-clock budget for the collection bar, measured from the click rather
     than from arrival, so a 200ms navigation and a 900ms one both end on a
     full bar. Must match --ka-veil-dur in ka-motion.css. */
  const BAR_MS = 450; // D52 row 5: bar 450 + fade 300 = a 750 ms page change
  const HOLD_MS = 0; // bar sits full — the completion beat

  /* Wall-clock budget for the product reticle's lock-on-and-shot timeline:
     circle scale-in + crosshair fade (0-600ms), tick pulse / lock
     confirmation (600-800ms), the shot -- circle blink, wrapper recoil,
     hitmark pop-in (800-1000ms). Must match the keyframe delays on
     .ka-veil__reticle-circle / -tick / .ka-veil__hitmark in ka-motion.css. */
  const RETICLE_MS = 450;
  const RETICLE_HOLD_MS = 0; // settle beat after the shot, before the fade

  const FADE_MS = 300; // veil opacity out
  const STEP_MS = 0; // gap between entrance groups
  const REVEAL_MS = 500; // longest entrance; matches --ka-t-slow
  const MAX_MS = 5000; // hard ceiling, must match the head script's MAX

  const VT_TYPE = 'ka-veil-nav';

  /* Optional locale prefix, then exactly one path segment under /collections.
     Deliberately NOT matching /collections/x/products/y — the `within:` form
     is a product URL that happens to live under a collection path and gets
     PRODUCT_WITHIN_PATH below instead. */
  const COLLECTION_PATH = /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/collections\/[^/]+\/?$/i;

  /* Plain product URL, optionally locale-prefixed. */
  const PRODUCT_PATH = /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/products\/[^/]+\/?$/i;

  /* Collection-scoped ("within:") product URL: /collections/x/products/y.
     Still a product page — Shopify just carries the collection in the path
     — so it gets the reticle too, not the bar. */
  const PRODUCT_WITHIN_PATH =
    /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/collections\/[^/]+\/products\/[^/]+\/?$/i;

  /* Same-origin but must never be veiled. Checkout leaves the theme entirely:
     no pagereveal, no arrival script, nothing to ever lift the veil. */
  const BLOCKED_PATH = /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/(?:cart|checkout|checkouts|account|orders|tools)(?:\/|$)/i;

  /* Unit L: generic page navigations get the logo veil instead of the bar
     or reticle. Originally scoped to four buckets with home ("/") and the
     bare collections index deliberately left un-veiled; Unit B (round 5)
     adds both of those in below -- 404s and app routes are still nothing
     here names them, so they stay un-veiled. Blog articles are one path
     segment deeper than the blog index, hence the optional second group. */
  const PAGE_PATH =
    /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/(?:pages\/[^/]+|blogs\/[^/]+(?:\/[^/]+)?|policies\/[^/]+|search)\/?$/i;

  /* Home ("/", or a bare locale prefix "/en-us/") and the bare collections
     index ("/collections", "/collections/"). Kept as separate matchers from
     PAGE_PATH rather than folded in, so each stays easy to verify on its
     own. Both are disjoint from COLLECTION_PATH above, which requires a
     segment after /collections/, so match order against it doesn't matter. */
  const HOME_PATH = /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/?$/;
  const COLLECTIONS_INDEX_PATH = /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/collections\/?$/i;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

  /** sessionStorage throws in some privacy modes; treat any failure as "no handshake". */
  const store = {
    get() {
      try {
        const raw = sessionStorage.getItem(KEY);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    },
    set(value) {
      try {
        sessionStorage.setItem(KEY, JSON.stringify(value));
        return true;
      } catch {
        return false;
      }
    },
    clear() {
      try {
        sessionStorage.removeItem(KEY);
      } catch {
        /* nothing to do */
      }
    },
  };

  /* =====================================================================
     Click side
     ===================================================================== */

  const isPlainLeftClick = (event) =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  const BAR_MARKUP =
    '<span class="ka-veil__label">Loading collection</span>' +
    '<span class="ka-veil__bar"><span class="ka-veil__bar-fill"></span></span>';

  /* "Acquiring" — the only copy this veil is allowed: the destination
     product's title isn't available pre-navigation, and inventing marketing
     copy here isn't this theme's call to make (see AGENT-BRIEF.md).

     v3 (Unit C, round 5) replaces the random closed-loop wander with a
     deterministic lock-on-and-shot timeline, driven entirely by CSS
     keyframes in ka-motion.css at fixed delays, full replay from 0 on every
     arrival (round 6) -- there is nothing left for JS to compute or
     randomize, so the markup is just the static chrome plus one new
     hitmark span for the "shot" beat. */
  const buildReticleMarkup = () => {
    const chrome =
      '<span class="ka-veil__reticle-line ka-veil__reticle-line--h"></span>' +
      '<span class="ka-veil__reticle-line ka-veil__reticle-line--v"></span>' +
      '<span class="ka-veil__reticle-circle"></span>' +
      '<span class="ka-veil__reticle-tick"></span>' +
      '<span class="ka-veil__hitmark"><span></span><span></span></span>';
    return (
      '<span class="ka-veil__reticle" aria-hidden="true">' + chrome + '</span>' +
      '<span class="ka-veil__label">Acquiring</span>'
    );
  };

  /*
    Logo veil markup (round 6: full-width wordmark). The URL comes from
    data-ka-logo on <html>, rendered server-side in layout/theme.liquid
    (settings.logo, the KA_Tech_Lock_Up wordmark, falling back to
    settings.favicon) -- this script can't run Liquid filters, so the
    attribute is the hand-off. No visible label: role="status" +
    aria-live="polite" is already on the veil element, so an aria-label
    stands in for the visible text the bar/reticle carry.

    This markup is shared by both the OUTGOING (click-side, built here) and
    the ARRIVAL (built by the head script in layout/theme.liquid) veils --
    round 7's .ka-veil--quiet (see buildVeil below) hides the <img> itself
    on the outgoing side, so only the arrival copy ever actually fades in.
  */
  const buildLogoMarkup = () => {
    const src = document.documentElement.dataset.kaLogo || '';
    return '<img class="ka-veil__logo" src="' + src + '" alt="" aria-hidden="true">';
  };

  /**
   * Round 7: outgoing (click-side) veil, always quiet. Lincoln watched the
   * arrival replay from 0 land directly behind an outgoing veil that had
   * already played its own full entrance -- worst on the logo veil, where
   * two complete 400ms fade-ins ran back to back and read as a rewind. The
   * fix is not timing, it's presence: the outgoing veil shows only static
   * chrome (label, and for collection the empty bar track) so there is
   * nothing on this side left to "replay" when the arrival's real sequence
   * starts. ka-motion.css's .ka-veil--quiet rule hides every animated
   * child (bar fill, reticle chrome, hitmark, logo image) by selector --
   * the markup itself is untouched and identical to what the arrival
   * builds, so the two stay hand-synced for free.
   *
   * @param {'collection' | 'product' | 'page'} kind
   */
  const buildVeil = (kind) => {
    const veil = document.createElement('div');
    const classes = ['ka-veil', 'ka-veil--quiet'];
    if (kind === 'product') classes.push('ka-veil--scope');
    veil.className = classes.join(' ');
    veil.setAttribute('role', 'status');
    veil.setAttribute('aria-live', 'polite');
    if (kind === 'page') veil.setAttribute('aria-label', 'Loading');
    veil.innerHTML =
      kind === 'product' ? buildReticleMarkup() : kind === 'page' ? buildLogoMarkup() : BAR_MARKUP;
    return veil;
  };

  /**
   * Every rejection here is a real failure mode found in this theme, not
   * defensive boilerplate. See the notes on each.
   * @param {HTMLAnchorElement} link
   * @returns {{ target: URL, kind: 'collection' | 'product' | 'page' } | null}
   */
  const resolveIntroTarget = (link) => {
    if (link.target && link.target !== '_self') return null;
    if (link.hasAttribute('download')) return null;
    if (link.rel && /\bexternal\b/i.test(link.rel)) return null;
    if (link.closest('[data-no-intro]')) return null;

    const raw = link.getAttribute('href');
    if (!raw || raw.charAt(0) === '#') return null;

    let target;
    try {
      target = new URL(link.href, window.location.href);
    } catch {
      return null;
    }

    // Rejects cross-origin, and also mailto:/tel:/sms: — their origin is the
    // string "null", never a match. ka-location.liquid ships an sms: link.
    if (target.origin !== window.location.origin) return null;

    /*
      Load-bearing, not a nicety. This single check covers four separate
      cases that would otherwise each veil a navigation that never happens
      or never should:
        - sort, filter and ?page=2, which reload the same collection
        - href="#" placeholders (ka-collection-grid, ka-comparison,
          ka-rifle-breakdown-panel all default to '#')
        - <a> with no href at all (ka-spotlight renders one when link_url is
          blank) — link.href resolves to the current page
        - any link back to the page you are already on
    */
    if (target.pathname === window.location.pathname) return null;

    if (BLOCKED_PATH.test(target.pathname)) return null;

    if (COLLECTION_PATH.test(target.pathname)) return { target, kind: 'collection' };
    if (PRODUCT_PATH.test(target.pathname) || PRODUCT_WITHIN_PATH.test(target.pathname)) {
      return { target, kind: 'product' };
    }
    if (
      PAGE_PATH.test(target.pathname) ||
      HOME_PATH.test(target.pathname) ||
      COLLECTIONS_INDEX_PATH.test(target.pathname)
    ) {
      return { target, kind: 'page' };
    }

    return null;
  };

  const onIntroClick = (event) => {
    if (!isPlainLeftClick(event) || event.defaultPrevented) return;
    if (inDesignMode) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    const resolved = resolveIntroTarget(link);
    if (!resolved) return;
    const { target, kind } = resolved;

    // No sessionStorage means no arrival choreography is possible; let the
    // browser navigate normally rather than showing a veil that never lifts.
    if (!store.set({ t: Date.now(), to: target.pathname, kind })) return;

    // Suppress Horizon's page-navigation crossfade. See the header comment.
    link.setAttribute('data-view-transition-type', VT_TYPE);
    link.setAttribute('data-view-transition-triggered', 'true');

    /*
      product-card-link.js binds its own click listener directly on
      <product-card-link> (a closer ancestor of the click target than
      document), so it fires before this one and has already stamped
      data-view-transition-type="product-image-transition" on the card's
      gallery element for the built-in image fly-to-PDP morph. That morph
      is intentionally replaced by the reticle veil on these clicks. Our
      attribute above already wins on DOM order — every card template this
      theme ships puts the <a> before its gallery child — but clear theirs
      explicitly rather than depend on that, since view-transitions.js only
      reads the FIRST [data-view-transition-triggered] it finds.
    */
    if (kind === 'product') {
      const cardLink = link.closest('product-card-link');
      if (cardLink) {
        cardLink.querySelectorAll('[data-view-transition-triggered]').forEach((el) => {
          if (el === link) return;
          el.removeAttribute('data-view-transition-triggered');
          el.removeAttribute('data-view-transition-type');
        });
      }
    }

    // Handshake is set either way — the arrival side still stages the content,
    // just without an animated veil in front of it.
    if (reduced) return;

    /*
      Bug fix B (round 7): a click landing while an arrival veil is still up
      (built by theme.liquid's head script, not yet faded via .ka-veil--out)
      would otherwise stack a second, quiet veil on top of it -- the
      handshake above is already written and the view-transition attributes
      are already stamped regardless, so the navigation and the arrival
      choreography on the OTHER end are unaffected. Only the redundant
      visible veil is skipped here.
    */
    if (document.querySelector('.ka-veil:not(.ka-veil--out)')) return;

    document.body.appendChild(buildVeil(kind));
  };

  document.addEventListener('click', onIntroClick, { capture: false });

  /* =====================================================================
     Arrival side

     The head script in layout/theme.liquid has already decided whether this
     visit is a staged arrival, added ka-intro-armed, locked scroll and put
     the veil up — all before first paint. If it did not, there is nothing
     for us to do.
     ===================================================================== */

  const root = document.documentElement;
  if (!root.classList.contains('ka-intro-armed')) {
    // Not staged, but a stale key could still fire on the next navigation.
    store.clear();
    return;
  }

  const handshake = store.get();
  store.clear(); // one-shot: a refresh or a back-nav must not replay it

  /*
    Round 7: kind now comes off the DOM, not the handshake object. A
    back/forward navigation never wrote a handshake (there was no click,
    sessionStorage is empty here) -- theme.liquid's head script arms that
    path directly and hands off the kind via data-ka-intro-kind since it's
    the only channel available. Forward navs still fall back to
    handshake.kind for belt-and-suspenders, but dataset is the source of
    truth: theme.liquid sets it in every armed case, not just the back one.
  */
  const introKind = root.dataset.kaIntroKind || (handshake && handshake.kind) || null;
  const isBackNav = root.dataset.kaIntroBack === '1';

  const veil = document.querySelector('.ka-veil');

  /*
    Queried from the document, not from within [data-ka-intro-stage]. The
    header lives outside every section, and two page templates
    (page.tactical-gear, page.contact) render two main sections — the old
    single-element querySelector would only ever have staged the first.
  */
  const groups = Array.from(document.querySelectorAll('[data-ka-intro-step]')).sort(
    (a, b) => Number(a.dataset.kaIntroStep) - Number(b.dataset.kaIntroStep)
  );

  const removeVeil = () => {
    if (!veil) return;
    veil.classList.add('ka-veil--out');
    veil.addEventListener('transitionend', () => veil.remove(), { once: true });
    setTimeout(() => veil.remove(), FADE_MS + 300);
  };

  const unlock = () => root.classList.remove('ka-scroll-lock');

  /*
    H9-E is gone. It existed because .ka-header-controls (MENU / SEARCH) used
    to be position:fixed in a DIFFERENT section and had to be told when the
    header's entrance transform had cleared so it could re-measure. The
    controls are now real in-flow children of the header row (rendered by
    snippets/ka-header-controls.liquid into snippets/header-row.liquid), so
    they ride the entrance with the header and there is nothing to notify --
    the `ka:intro-done` event and its transitionend fast path went with it.
  */
  const disarm = () => {
    root.classList.remove('ka-intro-armed', 'ka-intro-product');
    delete root.dataset.kaIntroKind;
    delete root.dataset.kaIntroBack;
  };

  /*
    Reveals one staged group AND, once its own slide has landed, the
    [data-ka-anim] elements inside it (PDP gallery thumbnails, title/price,
    option groups, buy buttons, accordion items -- see
    sections/ka-product-premium.liquid). ka-motion.js's IntersectionObserver
    skips these descendants while their ancestor group is still armed and
    unrevealed (see the guard there), so this is the only place that ever
    reveals them for a staged arrival. Delayed by REVEAL_MS so the inner
    cascade starts after the group's own movement finishes rather than
    racing it -- "the group slides in, then its contents cascade", not both
    at once.
  */
  const revealGroup = (el) => {
    el.classList.add('is-in');
    window.kaMotion?.reveal(el);
    const inner = el.querySelectorAll('[data-ka-anim]');
    if (!inner.length) return;
    setTimeout(() => {
      inner.forEach((child) => window.kaMotion?.reveal(child));
    }, REVEAL_MS);
  };

  const revealAll = () => {
    groups.forEach(revealGroup);
  };

  /* Declared outside the async gate below so bfcache restore (which can fire
     before the gate resolves) always has a real function to call, never a
     ReferenceError from a not-yet-hoisted const. */
  let failsafe = null;
  const stopFailsafe = () => {
    if (failsafe) clearTimeout(failsafe);
  };

  /*
    Cold-cache FOUT guard. The hero title (data-ka-intro-step) is about to
    reveal, and on a cold cache the Bebas Neue / Zalando Sans <link> is still
    media="print" (see snippets/fonts.liquid) -- so a reveal that starts
    before the swap paints Arial, then jumps to the real face mid-animation.

    document.fonts.ready is NOT the right gate here: it resolves as soon as
    the current font set has finished loading, which on this exact cold path
    is immediately, before the print-media stylesheet has even swapped in --
    a no-op. document.fonts.load() requests the family directly regardless of
    which stylesheet declared it, so it actually waits for the file.

    Capped at 600ms and raced rather than awaited outright, so a slow or
    failed font fetch can never block the reveal past the existing MAX_MS
    failsafe below -- worst case this adds 600ms, comfortably inside it.
    Wrapped in try/catch: an unknown family string is a real way for
    `fonts.load` to throw, not a hypothetical.
  */
  /*
    Shared tail every kind runs once its own "fill" gate has resolved --
    bar/reticle timing for collection/product, the page-kind readiness gate
    below for page: veil fades at fadeAt, staged groups reveal starting
    FADE_MS later, and the arrival disarms once the last one has actually
    finished landing.
  */
  const runStandardSequence = (fadeAt) => {
    const revealAt = fadeAt + FADE_MS;

    setTimeout(() => {
      removeVeil();
      unlock();
    }, fadeAt);

    groups.forEach((el, i) => {
      setTimeout(() => revealGroup(el), revealAt + i * STEP_MS);
    });

    /*
      Disarm only after the last entrance has actually finished. The staging
      rules -- including the transition that makes the entrance a movement at
      all -- live under html.ka-intro-armed, so dropping the class mid-flight
      removes the transition with it and the element snaps to its final
      position instead of easing there.
    */
    const lastEnds = revealAt + (groups.length - 1) * STEP_MS + REVEAL_MS + 80;
    setTimeout(() => {
      stopFailsafe();
      disarm();
    }, lastEnds);
  };

  const armReveal = async () => {
    const arrivalStart = Date.now();

    try {
      await Promise.race([
        Promise.all([
          document.fonts.load('700 72px "Bebas Neue"'),
          document.fonts.load('400 1rem "Zalando Sans"'),
        ]),
        new Promise((resolve) => setTimeout(resolve, 200)),
      ]);
    } catch {
      /* fonts.load rejected/threw -- proceed with whatever is loaded */
    }

    /* Everything below can fail; this cannot. The page is never left locked
       or blank, whatever happens to the sequence. */
    failsafe = setTimeout(() => {
      revealAll();
      removeVeil();
      unlock();
      disarm();
    }, MAX_MS);

    if (reduced || !introKind) {
      stopFailsafe();
      revealAll();
      removeVeil();
      unlock();
      disarm();
      return;
    }

    if (introKind === 'page') {
      /*
        Page-kind arrival (Unit B, round 5). The logo veil has no bar/reticle
        fill to time the reveal against, so instead of racing a fixed
        wall-clock budget from the click it waits for the destination to
        actually be ready -- window 'load' (or immediately, if the document
        is already complete) capped at 2500ms so a slow or missing load event
        can never hang it -- AND holds the veil on screen for a minimum floor
        measured from when this script started running, so a cached, instant
        arrival never flashes the logo for a single frame. Whichever finishes
        last gates the same 140ms beat + 300ms fade + reveal tail every kind
        shares (runStandardSequence above).

        Round 7: the floor is shorter on a back/forward arrival (400ms vs
        900ms forward). The visitor has already seen this page once this
        session -- there's no new content worth a full branded hold, just
        enough of a beat that the logo reads as a deliberate loading state
        rather than a flash.

        Round 8 (B8-3): the load-race CAP is now also shorter on a
        back/forward arrival -- 1500ms vs 2500ms forward. Measured
        real-world back-loads run ~1730ms; a 2.5s worst-case hold on a
        cache-miss back nav reads as broken (blank white veil, no bar/
        reticle to justify the wait), so the cap can't stay at the forward
        figure. Floor stays 400ms; worst case is now
        1500 + 140 (hold) + 300 (fade) = 1940ms, comfortably under 2s.
      */
      const loadRaceCapMs = 0; /* R34 row 13: no load wait on the veil */
      await Promise.race([
        new Promise((resolve) => {
          if (document.readyState === 'complete') {
            resolve();
            return;
          }
          window.addEventListener('load', () => resolve(), { once: true });
        }),
        new Promise((resolve) => setTimeout(resolve, loadRaceCapMs)),
      ]);

      const holdFloorMs = 0;
      const minRemaining = Math.max(0, holdFloorMs - (Date.now() - arrivalStart));
      if (minRemaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, minRemaining));
      }

      runStandardSequence(HOLD_MS);
      return;
    }

    /*
      Collection and product arrivals, full replay from 0 (round 6):

        0 .......... fillMs   bar fills to 100% (or reticle assembles)
        + holdMs              bar/reticle sits still  <- the completion beat
        + FADE_MS              veil fades to an EMPTY page
        then                   header, hero/gallery, copy/buybox, toolbar/accordion

      Scroll unlocks the moment the veil is gone rather than at the end, so
      the visitor has control back while the entrances finish behind them.

      This used to subtract time-since-click (elapsed) from fillMs, so a slow
      navigation played a shorter fill on arrival -- the CSS side resumed a
      negative-delay animation to match. That died on real navigation timing:
      Chrome paint-holds the outgoing page at nav commit, so the arrival
      paints only once the browser is ready to show it, by which point the
      resumed animation had often already run past its own end. Lincoln's
      call: every arrival plays the complete fill from 0, always -- fillMs is
      now used directly, not reduced by anything.
    */
    const usesReticleTiming = introKind === 'product';
    const fillMs = usesReticleTiming ? RETICLE_MS : BAR_MS;
    const holdMs = usesReticleTiming ? RETICLE_HOLD_MS : HOLD_MS;

    const fillRemaining = fillMs;

    runStandardSequence(fillRemaining + holdMs);
  };

  armReveal();

  // bfcache restore must never show a stale veil or a locked page.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    stopFailsafe();
    revealAll();
    removeVeil();
    unlock();
    disarm();
  });
})();
